import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Keyboard-first navigation for any list view.
 * Bindings (Linear / GitHub style):
 *   /            focus the first input with data-kbd-search
 *   j            move selection down
 *   k            move selection up
 *   o, Enter     open current item (calls onOpen or nav(url))
 *   g g          jump to top
 *   Shift+G      jump to bottom
 *   Esc          clear selection / blur search
 *
 * Usage:
 *   const { index, setIndex, getRowProps } = useListKeyboardNav({
 *     count: items.length,
 *     getUrl: i => `/app/cve/${items[i].cve_id}`,
 *   })
 *   {items.map((it, i) => <div {...getRowProps(i)}>…</div>)}
 *
 * Or pass onOpen for custom behaviour.
 */
export function useListKeyboardNav({
  count, getUrl, onOpen, enabled = true,
}: {
  count: number
  getUrl?: (i: number) => string | undefined
  onOpen?: (i: number) => void
  enabled?: boolean
}) {
  const [index, setIndex] = useState<number>(-1)
  const rowRefs = useRef<(HTMLElement | null)[]>([])
  const lastGKey = useRef<number>(0)
  const nav = useNavigate()

  // Keep refs array sized with item count
  useEffect(() => { rowRefs.current = rowRefs.current.slice(0, count) }, [count])

  const scrollIntoView = useCallback((i: number) => {
    const el = rowRefs.current[i]
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [])

  const doOpen = useCallback((i: number) => {
    if (i < 0 || i >= count) return
    if (onOpen) { onOpen(i); return }
    const url = getUrl?.(i)
    if (url) {
      if (url.startsWith('http')) window.open(url, '_blank', 'noopener')
      else nav(url)
    }
  }, [count, onOpen, getUrl, nav])

  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      // Ignore keystrokes targeted at inputs — unless the key is Esc
      const target = e.target as HTMLElement | null
      const inEditable = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
      if (inEditable && e.key !== 'Escape') return

      // Respect common mod keys — don't eat ⌘K / Ctrl+K etc.
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === '/') {
        const s = document.querySelector<HTMLInputElement>('[data-kbd-search]')
        if (s) { e.preventDefault(); s.focus(); s.select?.() }
        return
      }
      if (e.key === 'Escape') {
        if (inEditable) (target as HTMLInputElement).blur()
        setIndex(-1)
        return
      }
      if (e.key === 'j') {
        e.preventDefault()
        setIndex(i => {
          const next = Math.min(count - 1, Math.max(0, i + 1))
          scrollIntoView(next)
          return next
        })
        return
      }
      if (e.key === 'k') {
        e.preventDefault()
        setIndex(i => {
          const next = Math.max(0, i - 1)
          scrollIntoView(next)
          return next
        })
        return
      }
      if (e.key === 'o' || e.key === 'Enter') {
        if (index >= 0) { e.preventDefault(); doOpen(index) }
        return
      }
      if (e.key === 'g') {
        const now = Date.now()
        if (now - lastGKey.current < 500) {
          e.preventDefault()
          setIndex(count > 0 ? 0 : -1)
          scrollIntoView(0)
          lastGKey.current = 0
        } else {
          lastGKey.current = now
        }
        return
      }
      if (e.key === 'G' && e.shiftKey) {
        e.preventDefault()
        const last = count - 1
        setIndex(last)
        scrollIntoView(last)
        return
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled, count, index, scrollIntoView, doOpen])

  const getRowProps = useCallback((i: number) => ({
    ref: (el: HTMLElement | null) => { rowRefs.current[i] = el },
    'data-kbd-row': i,
    'aria-selected': i === index,
    onMouseEnter: () => setIndex(i),
    className: i === index ? 'kbd-focused' : '',
  }), [index])

  return { index, setIndex, getRowProps }
}
